/**
 * SelectListAggregateSectionPrompt — section-level prompt rendered in the SELECT
 * ClauseCard header when the query mixes aggregate and non-aggregate columns and
 * one or more non-aggregate columns are not yet in GROUP BY. It complements the
 * per-row warning icons (`SelectListAggregateWarning`): the per-row icon adds one
 * column, this prompt's "Fix all" button adds every missing column in one click.
 *
 * "Fix all" loops the missing-column list and commits `addGroupByExpression` for
 * each — the same edit + arg the per-row icon uses. After the first fix the list
 * is empty, so a second click is an idempotent no-op.
 *
 * Copy comes from `vqeCopy` (hint + actions); analytics fires
 * `feature:vqe_aggregate_fix_all { count }` (low-cardinality) on click.
 */

import { Button } from '../../../../../components/ui/button';
import { useAnalytics } from '../../../../../core/analytics/useAnalytics';
import type { VqeEditContext } from '../../edit/editContext';
import { addGroupByExpression } from '../../edit/edits/addGroupBy';
import { runSequentialStatementEdits } from '../../edit/runSequentialStatementEdits';
import { vqeCopy } from '../../ux/vqeCopy';

export interface SelectListAggregateSectionPromptProps {
  readonly editContext: VqeEditContext;
  /** Expression texts of the non-aggregate columns missing from GROUP BY. */
  readonly missingColumns: readonly string[];
}

export function SelectListAggregateSectionPrompt({
  editContext,
  missingColumns,
}: SelectListAggregateSectionPromptProps) {
  const { track } = useAnalytics();
  if (missingColumns.length === 0) return null;

  const onFixAll = () => {
    // Snapshot the list before committing — the loop is captured at click time
    // so it stays stable even as commits mutate the buffer underneath.
    const targets = [...missingColumns];
    track('feature:vqe_aggregate_fix_all', { count: targets.length });
    void fixAllSequential(editContext, targets);
  };

  return (
    <span
      role="note"
      data-testid="vqe-aggregate-section-prompt"
      className="flex items-center gap-2 rounded border border-amber-300/60 bg-amber-50 px-2 py-0.5 text-[11px] font-normal normal-case tracking-normal text-amber-800 dark:border-amber-500/40 dark:bg-amber-950/40 dark:text-amber-200"
    >
      <span className="min-w-0">{vqeCopy.hint.aggregateNotInGroupBy(missingColumns.length)}</span>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="h-5 px-1.5 text-[11px] font-medium text-amber-800 hover:text-amber-900 dark:text-amber-200 dark:hover:text-amber-100"
        data-testid="vqe-aggregate-fix-all"
        onClick={onFixAll}
      >
        {vqeCopy.actions.fixAllGroupBy}
      </Button>
    </span>
  );
}

/**
 * Commit one `addGroupBy` per missing column, sequentially, re-resolving the
 * statement id before each commit (ids rotate on each re-parse). See
 * `runSequentialStatementEdits` for the why.
 */
async function fixAllSequential(
  editContext: VqeEditContext,
  columnTexts: readonly string[],
): Promise<void> {
  await runSequentialStatementEdits(editContext, columnTexts, (text, ctx) =>
    addGroupByExpression(text, ctx),
  );
}
